import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Image, FileText, Mail, Plus, Trash2, Check, Edit3, CalendarDays, Building2, Layout as LayoutIcon, KeyRound } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import EncoreWordmark from "../components/EncoreWordmark";
import PageContentAdmin from "./PageContentAdmin";
import ImageUploadField from "../components/ImageUploadField";

const TABS = [
  { id: "page", label: "Home Page", icon: LayoutIcon },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "articles", label: "Articles", icon: FileText },
  { id: "bookings", label: "Bookings", icon: Building2 },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "account", label: "Account", icon: KeyRound },
];

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("page");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user)
    return (
      <div className="min-h-screen flex items-center justify-center font-overline text-sm text-stone-400">
        Loading…
      </div>
    );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#372C2E]">
      {/* Header */}
      <header className="border-b border-white/10 bg-stone-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="admin-home-link"
              aria-label="Encore — back to public site"
            >
              <img
                src="/brand/encore-wordmark.png"
                alt="Encore Theatre and Gallery"
                className="h-9 w-auto"
                draggable={false}
              />
            </Link>
            <span className="font-overline text-xs text-stone-400">
              Admin Console
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="font-overline text-xs text-stone-400 hidden md:inline">
              {user.username}
            </span>
            <button
              data-testid="admin-logout"
              onClick={async () => {
                await logout();
                navigate("/admin/login", { replace: true });
              }}
              className="btn-outline !py-2 !px-4 !text-[0.65rem]"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 mb-10 border-b border-white/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 font-overline text-xs border-b-2 transition-colors ${
                  tab === t.id
                    ? "border-[#DE9E48] text-[#DE9E48]"
                    : "border-transparent text-stone-400 hover:text-white"
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "page" && <PageContentAdmin />}
        {tab === "gallery" && <GalleryAdmin />}
        {tab === "events" && <EventsAdmin />}
        {tab === "articles" && <ArticlesAdmin />}
        {tab === "bookings" && <BookingsAdmin />}
        {tab === "messages" && <MessagesAdmin />}
        {tab === "account" && <AccountAdmin />}
      </div>
    </div>
  );
}

// ---------------- Gallery Admin ----------------
function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ url: "", caption: "", category: "performance", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(
    () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([])),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editingId) {
        await api.put(`/admin/gallery/${editingId}`, payload);
      } else {
        await api.post("/admin/gallery", payload);
      }
      setForm({ url: "", caption: "", category: "performance", order: 0 });
      setEditingId(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  const startEdit = (g) => {
    setEditingId(g.id);
    setForm({
      url: g.url,
      caption: g.caption || "",
      category: g.category || "performance",
      order: g.order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <form
        data-testid="gallery-admin-form"
        onSubmit={submit}
        className="lg:col-span-4 border border-white/10 bg-stone-900/40 p-6 h-fit sticky top-28"
      >
        <h3 className="font-display text-2xl mb-6">
          {editingId ? "Edit Image" : "Add Image"}
        </h3>

        <div className="mb-5">
          <ImageUploadField
            label="Image"
            testid="gallery-image"
            required
            value={form.url}
            onChange={(v) => setForm({ ...form, url: v })}
          />
        </div>
        <div className="mb-5">
          <label className="field-label">Caption</label>
          <input
            data-testid="gallery-caption-input"
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
            className="field-input"
            placeholder="Optional"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="field-label">Category</label>
            <input
              data-testid="gallery-category-input"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="field-input"
            />
          </div>
          <div>
            <label className="field-label">Order</label>
            <input
              data-testid="gallery-order-input"
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
              className="field-input"
            />
          </div>
        </div>

        <button data-testid="gallery-submit" type="submit" className="btn-gold w-full justify-center">
          <Plus size={14} /> {editingId ? "Update Image" : "Add Image"}
        </button>
        {editingId && (
          <button
            type="button"
            data-testid="gallery-cancel-edit"
            onClick={() => {
              setEditingId(null);
              setForm({ url: "", caption: "", category: "performance", order: 0 });
            }}
            className="btn-outline w-full justify-center mt-3"
          >
            Cancel
          </button>
        )}
        {error && (
          <p className="mt-4 font-overline text-xs text-red-400">{error}</p>
        )}
      </form>

      <div className="lg:col-span-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl">
            Gallery <span className="text-stone-500">({items.length})</span>
          </h3>
        </div>
        {items.length === 0 ? (
          <p className="font-overline text-xs text-stone-500 py-10">No images.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((g, i) => (
              <div
                key={g.id}
                data-testid={`admin-gallery-item-${i}`}
                className="relative border border-white/10 bg-stone-900/40 group"
              >
                <div className="aspect-square bg-stone-950 overflow-hidden">
                  <img src={g.url} alt={g.caption || ""} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <p className="font-overline text-[0.65rem] text-stone-400 truncate">
                    #{g.order} • {g.category}
                  </p>
                  <div className="flex gap-1">
                    <button
                      data-testid={`admin-gallery-edit-${i}`}
                      onClick={() => startEdit(g)}
                      className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-[#DE9E48]"
                    >
                      <Edit3 size={13} />
                    </button>
                    <button
                      data-testid={`admin-gallery-delete-${i}`}
                      onClick={() => remove(g.id)}
                      className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-red-400"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Articles Admin ----------------
function ArticlesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", link: "", image_url: "" });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(
    () => api.get("/articles").then((r) => setItems(r.data)).catch(() => setItems([])),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (editingId) {
      await api.put(`/admin/articles/${editingId}`, form);
    } else {
      await api.post("/admin/articles", form);
    }
    setForm({ title: "", excerpt: "", link: "", image_url: "" });
    setEditingId(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`);
    load();
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title,
      excerpt: a.excerpt || "",
      link: a.link || "",
      image_url: a.image_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <form
        data-testid="articles-admin-form"
        onSubmit={submit}
        className="lg:col-span-5 border border-white/10 bg-stone-900/40 p-6 h-fit sticky top-28"
      >
        <h3 className="font-display text-2xl mb-6">
          {editingId ? "Edit Article" : "Add Article"}
        </h3>
        <div className="mb-5">
          <label className="field-label">Title</label>
          <input
            data-testid="article-title-input"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="field-input"
          />
        </div>
        <div className="mb-5">
          <label className="field-label">Excerpt</label>
          <textarea
            data-testid="article-excerpt-input"
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            className="field-textarea"
            rows={3}
          />
        </div>
        <div className="mb-5">
          <label className="field-label">Link</label>
          <input
            data-testid="article-link-input"
            value={form.link}
            onChange={(e) => setForm({ ...form, link: e.target.value })}
            className="field-input"
            placeholder="https://…"
          />
        </div>
        <div className="mb-6">
          <ImageUploadField
            label="Article image"
            testid="article-image"
            value={form.image_url}
            onChange={(v) => setForm({ ...form, image_url: v })}
          />
        </div>
        <button data-testid="article-submit" type="submit" className="btn-gold w-full justify-center">
          <Plus size={14} /> {editingId ? "Update Article" : "Add Article"}
        </button>
        {editingId && (
          <button
            type="button"
            data-testid="article-cancel-edit"
            onClick={() => {
              setEditingId(null);
              setForm({ title: "", excerpt: "", link: "", image_url: "" });
            }}
            className="btn-outline w-full justify-center mt-3"
          >
            Cancel
          </button>
        )}
      </form>

      <div className="lg:col-span-7">
        <h3 className="font-display text-2xl mb-6">
          Articles <span className="text-stone-500">({items.length})</span>
        </h3>
        {items.length === 0 ? (
          <p className="font-overline text-xs text-stone-500 py-10">No articles.</p>
        ) : (
          <div className="space-y-4">
            {items.map((a, i) => (
              <div
                key={a.id}
                data-testid={`admin-article-${i}`}
                className="border border-white/10 bg-stone-900/40 p-5 flex gap-5 items-start"
              >
                {a.image_url && (
                  <img
                    src={a.image_url}
                    alt={a.title}
                    className="w-28 h-20 object-cover bg-stone-950 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-display text-xl truncate">{a.title}</h4>
                  <p className="font-body text-sm text-stone-400 line-clamp-2 mt-1">
                    {a.excerpt}
                  </p>
                  {a.link && (
                    <a
                      href={a.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-overline text-[0.65rem] text-[#DE9E48] mt-2 inline-block"
                    >
                      {a.link.length > 50 ? a.link.slice(0, 50) + "…" : a.link}
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    data-testid={`admin-article-edit-${i}`}
                    onClick={() => startEdit(a)}
                    className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-[#DE9E48]"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    data-testid={`admin-article-delete-${i}`}
                    onClick={() => remove(a.id)}
                    className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Events Admin ----------------
function EventsAdmin() {
  const empty = {
    title: "",
    poster_url: "",
    event_date: "",
    event_time: "",
    venue: "",
    description: "",
    cta_url: "",
    order: 0,
  };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(
    () =>
      api
        .get("/admin/events")
        .then((r) => setItems(r.data))
        .catch(() => setItems([])),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, payload);
      } else {
        await api.post("/admin/events", payload);
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save event");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    load();
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      title: ev.title || "",
      poster_url: ev.poster_url || "",
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      venue: ev.venue || "",
      description: ev.description || "",
      cta_url: ev.cta_url || "",
      order: ev.order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <form
        data-testid="events-admin-form"
        onSubmit={submit}
        className="lg:col-span-5 border border-white/10 bg-stone-900/40 p-6 h-fit sticky top-28"
      >
        <h3 className="font-display text-2xl mb-6">
          {editingId ? "Edit Event" : "Add Event"}
        </h3>

        <div className="mb-5">
          <label className="field-label">Title</label>
          <input
            data-testid="event-title-input"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="field-input"
            placeholder="e.g. Inspire Our Poetry"
          />
        </div>

        <div className="mb-5">
          <ImageUploadField
            label="Event poster"
            testid="event-poster"
            value={form.poster_url}
            onChange={(v) => setForm({ ...form, poster_url: v })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="field-label">Date</label>
            <input
              data-testid="event-date-input"
              required
              type="date"
              value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              className="field-input"
              style={{ colorScheme: "dark" }}
            />
          </div>
          <div>
            <label className="field-label">Time</label>
            <input
              data-testid="event-time-input"
              value={form.event_time}
              onChange={(e) => setForm({ ...form, event_time: e.target.value })}
              className="field-input"
              placeholder="7:30 PM"
            />
          </div>
        </div>

        <div className="mb-5">
          <label className="field-label">Venue</label>
          <input
            data-testid="event-venue-input"
            value={form.venue}
            onChange={(e) => setForm({ ...form, venue: e.target.value })}
            className="field-input"
            placeholder="Encore Mainstage"
          />
        </div>

        <div className="mb-5">
          <label className="field-label">Description</label>
          <textarea
            data-testid="event-desc-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="field-textarea"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="col-span-2">
            <label className="field-label">CTA / Ticket URL</label>
            <input
              data-testid="event-cta-input"
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
              className="field-input"
              placeholder="https://encorebham.ludus.com/…"
            />
          </div>
          <div>
            <label className="field-label">Order</label>
            <input
              data-testid="event-order-input"
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
              className="field-input"
            />
          </div>
        </div>

        <button data-testid="event-submit" type="submit" className="btn-gold w-full justify-center">
          <Plus size={14} /> {editingId ? "Update Event" : "Add Event"}
        </button>
        {editingId && (
          <button
            type="button"
            data-testid="event-cancel-edit"
            onClick={() => {
              setEditingId(null);
              setForm(empty);
            }}
            className="btn-outline w-full justify-center mt-3"
          >
            Cancel
          </button>
        )}
        {error && (
          <p className="mt-4 font-overline text-xs text-red-400">{error}</p>
        )}
      </form>

      <div className="lg:col-span-7">
        <h3 className="font-display text-2xl mb-6">
          Events <span className="text-stone-500">({items.length})</span>
        </h3>
        {items.length === 0 ? (
          <p className="font-overline text-xs text-stone-500 py-10">No events yet.</p>
        ) : (
          <div className="space-y-4">
            {items.map((ev, i) => {
              const past = ev.event_date && ev.event_date < today;
              return (
                <div
                  key={ev.id}
                  data-testid={`admin-event-${i}`}
                  className={`border border-white/10 bg-stone-900/40 p-5 flex gap-5 items-start ${
                    past ? "opacity-50" : ""
                  }`}
                >
                  {ev.poster_url ? (
                    <img
                      src={ev.poster_url}
                      alt={ev.title}
                      className="w-24 h-32 object-cover bg-stone-950 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-24 h-32 bg-stone-950 flex items-center justify-center font-overline text-[0.6rem] text-stone-500 flex-shrink-0">
                      No Image
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-overline text-[0.65rem] text-[#DE9E48]">
                      {ev.event_date}
                      {ev.event_time && ` · ${ev.event_time}`}
                      {past && " · PAST"}
                    </p>
                    <h4 className="font-display text-xl mt-1 leading-tight">{ev.title}</h4>
                    {ev.venue && (
                      <p className="font-overline text-[0.65rem] text-stone-400 mt-1">
                        {ev.venue}
                      </p>
                    )}
                    {ev.description && (
                      <p className="font-body text-sm text-stone-400 mt-2 line-clamp-2">
                        {ev.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      data-testid={`admin-event-edit-${i}`}
                      onClick={() => startEdit(ev)}
                      className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-[#DE9E48]"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      data-testid={`admin-event-delete-${i}`}
                      onClick={() => remove(ev.id)}
                      className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------- Messages Admin ----------------
function MessagesAdmin() {
  const [items, setItems] = useState([]);

  const load = useCallback(
    () =>
      api
        .get("/admin/messages")
        .then((r) => setItems(r.data))
        .catch(() => setItems([])),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    await api.patch(`/admin/messages/${id}/read`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/messages/${id}`);
    load();
  };

  const unreadCount = items.filter((m) => !m.read).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h3 className="font-display text-2xl">
          Contact Messages{" "}
          <span className="text-stone-500">
            ({items.length} • {unreadCount} unread)
          </span>
        </h3>
      </div>

      {items.length === 0 ? (
        <p data-testid="admin-messages-empty" className="font-overline text-xs text-stone-500 py-10">
          No messages yet.
        </p>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Message</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m, i) => (
                <tr
                  key={m.id}
                  data-testid={`admin-message-${i}`}
                  className={m.read ? "opacity-60" : ""}
                >
                  <td>
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        m.read ? "bg-stone-600" : "bg-[#DE9E48]"
                      }`}
                    />
                  </td>
                  <td className="font-overline text-[0.65rem] text-stone-400 whitespace-nowrap">
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                  <td className="font-medium">{m.name}</td>
                  <td>
                    <a href={`mailto:${m.email}`} className="text-[#DE9E48] hover:underline">
                      {m.email}
                    </a>
                  </td>
                  <td className="max-w-[180px] truncate">{m.subject || "—"}</td>
                  <td className="max-w-[300px] text-stone-400 whitespace-pre-wrap">
                    {m.message}
                  </td>
                  <td className="whitespace-nowrap">
                    {!m.read && (
                      <button
                        data-testid={`admin-message-read-${i}`}
                        onClick={() => markRead(m.id)}
                        className="w-7 h-7 inline-flex items-center justify-center text-stone-400 hover:text-[#DE9E48]"
                        title="Mark as read"
                      >
                        <Check size={14} />
                      </button>
                    )}
                    <button
                      data-testid={`admin-message-delete-${i}`}
                      onClick={() => remove(m.id)}
                      className="w-7 h-7 inline-flex items-center justify-center text-stone-400 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Booking Inquiries Admin ----------------
const FORM_TYPE_META = {
  ticketed: { label: "Ticketed Event", color: "bg-amber-700/30 text-amber-300 border-amber-700/40" },
  private: { label: "Private Event", color: "bg-rose-700/30 text-rose-300 border-rose-700/40" },
  creative: { label: "Creative / Community", color: "bg-emerald-700/30 text-emerald-300 border-emerald-700/40" },
  legacy: { label: "Legacy", color: "bg-stone-700/40 text-stone-300 border-stone-600/40" },
};

function formatLabel(key) {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function formatValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  const s = String(v).trim();
  return s || "—";
}

function BookingsAdmin() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(
    () =>
      api
        .get("/admin/bookings")
        .then((r) => setItems(r.data))
        .catch(() => setItems([])),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    await api.patch(`/admin/bookings/${id}/read`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/bookings/${id}`);
    load();
  };

  const getDisplayName = (b) =>
    b.full_name || [b.first_name, b.last_name].filter(Boolean).join(" ") || "—";
  const getFormType = (b) => b.form_type || "legacy";
  const getTitle = (b) =>
    b.event_title || b.nature_of_event || (b.data && (b.data.event_title || b.data.project_name)) || "";

  const buildReplyHref = (b) => {
    const name = getDisplayName(b).split(" ")[0] || "there";
    const meta = FORM_TYPE_META[getFormType(b)] || FORM_TYPE_META.legacy;
    const subj = `Re: Encore ${meta.label} Inquiry${getTitle(b) ? ` — ${getTitle(b)}` : ""}`;
    const body =
      `Hi ${name},%0A%0A` +
      `Thank you for reaching out to Encore Theatre & Gallery about your event.%0A%0A` +
      `— The Encore Team`;
    return `mailto:${b.email}?cc=encorebham@gmail.com&subject=${encodeURIComponent(subj)}&body=${body}`;
  };

  const filtered = filter === "all" ? items : items.filter((b) => getFormType(b) === filter);
  const unreadCount = filtered.filter((m) => !m.read).length;
  const counts = {
    all: items.length,
    ticketed: items.filter((b) => getFormType(b) === "ticketed").length,
    private: items.filter((b) => getFormType(b) === "private").length,
    creative: items.filter((b) => getFormType(b) === "creative").length,
    legacy: items.filter((b) => getFormType(b) === "legacy").length,
  };

  const filterOptions = [
    { id: "all", label: "All" },
    { id: "ticketed", label: "Ticketed" },
    { id: "private", label: "Private" },
    { id: "creative", label: "Creative" },
    ...(counts.legacy > 0 ? [{ id: "legacy", label: "Legacy" }] : []),
  ];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h3 className="font-display text-2xl">
          Booking Inquiries{" "}
          <span className="text-stone-500">
            ({filtered.length} · {unreadCount} unread)
          </span>
        </h3>
        <p className="font-overline text-[0.65rem] text-stone-500">
          Auto-forwarded to <span className="text-[#DE9E48]">encorebham@gmail.com</span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-bookings-filter">
        {filterOptions.map((opt) => {
          const active = filter === opt.id;
          return (
            <button
              key={opt.id}
              data-testid={`admin-bookings-filter-${opt.id}`}
              onClick={() => setFilter(opt.id)}
              className={[
                "px-4 py-1.5 rounded-full font-overline text-[0.65rem] tracking-[0.18em] border transition-colors",
                active
                  ? "bg-[#DE9E48] border-[#DE9E48] text-white"
                  : "border-white/15 text-stone-400 hover:border-[#DE9E48]/60 hover:text-stone-100",
              ].join(" ")}
            >
              {opt.label} ({counts[opt.id]})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p data-testid="admin-bookings-empty" className="font-overline text-xs text-stone-500 py-10">
          No booking inquiries to show.
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map((b, i) => {
            const isOpen = expanded === b.id;
            const ftype = getFormType(b);
            const meta = FORM_TYPE_META[ftype] || FORM_TYPE_META.legacy;
            const data = b.data && typeof b.data === "object" ? b.data : null;
            return (
              <div
                key={b.id}
                data-testid={`admin-booking-${i}`}
                className={`border bg-stone-900/40 rounded-xl ${
                  b.read ? "border-white/5 opacity-70" : "border-[#DE9E48]/30"
                }`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : b.id)}
                  className="w-full p-5 flex items-start gap-5 text-left"
                >
                  <span
                    className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${
                      b.read ? "bg-stone-600" : "bg-[#DE9E48]"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span
                        className={`font-overline text-[0.6rem] tracking-[0.18em] border rounded-full px-2.5 py-0.5 ${meta.color}`}
                      >
                        {meta.label}
                      </span>
                      {getTitle(b) && (
                        <span className="font-overline text-[0.65rem] text-stone-400">
                          · {getTitle(b)}
                        </span>
                      )}
                    </div>
                    <h4 className="font-display text-xl leading-tight">
                      {getDisplayName(b)}
                      {(b.organization || b.company) && (
                        <span className="font-overline text-[0.65rem] text-stone-500 ml-2">
                          · {b.organization || b.company}
                        </span>
                      )}
                    </h4>
                    <p className="font-body text-sm text-stone-400 mt-1.5">
                      {b.email} · {b.phone || "—"}
                    </p>
                  </div>
                  <span className="font-overline text-[0.65rem] text-stone-500 whitespace-nowrap">
                    {new Date(b.created_at).toLocaleDateString()}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-white/10 p-5 md:p-6 space-y-4 bg-stone-950/40">
                    {ftype === "legacy" ? (
                      <>
                        {b.nature_of_event && <DetailRow label="Event Type" value={b.nature_of_event} />}
                        {b.proposed_dates && <DetailRow label="Proposed Dates" value={b.proposed_dates} />}
                        {b.estimated_guests && <DetailRow label="Est. Guests" value={b.estimated_guests} />}
                        {b.non_profit && <DetailRow label="Non-Profit" value={b.non_profit} />}
                        {b.event_details && <DetailRow label="Event Details" value={b.event_details} />}
                        {b.other_details && <DetailRow label="Other Details" value={b.other_details} />}
                        {b.referral && <DetailRow label="Referral" value={b.referral} />}
                      </>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                        {data &&
                          Object.entries(data)
                            .filter(([k]) => k !== "acknowledged")
                            .map(([k, v]) => {
                              if (k === "schedule" && Array.isArray(v) && v.length > 0) {
                                return <ScheduleDetail key={k} entries={v} />;
                              }
                              if (k === "attachments" && Array.isArray(v) && v.length > 0) {
                                return <AttachmentsDetail key={k} files={v} />;
                              }
                              return (
                                <DetailRow key={k} label={formatLabel(k)} value={formatValue(v)} />
                              );
                            })}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3 pt-4 border-t border-white/5">
                      <a
                        data-testid={`admin-booking-reply-${i}`}
                        href={buildReplyHref(b)}
                        className="btn-gold !py-2 !px-4 !text-[0.65rem]"
                      >
                        <Mail size={12} /> Reply via Email
                      </a>
                      {!b.read && (
                        <button
                          data-testid={`admin-booking-read-${i}`}
                          onClick={() => markRead(b.id)}
                          className="btn-outline !py-2 !px-4 !text-[0.65rem]"
                        >
                          <Check size={12} /> Mark Read
                        </button>
                      )}
                      <button
                        data-testid={`admin-booking-delete-${i}`}
                        onClick={() => remove(b.id)}
                        className="btn-outline !py-2 !px-4 !text-[0.65rem] !border-red-500/30 !text-red-400 hover:!bg-red-500/10"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="font-overline text-[0.65rem] text-[#DE9E48] mb-1">{label}</p>
      <p className="font-body text-sm text-stone-300 whitespace-pre-wrap leading-relaxed">
        {value}
      </p>
    </div>
  );
}

function ScheduleDetail({ entries }) {
  return (
    <div className="md:col-span-2" data-testid="admin-booking-schedule">
      <p className="font-overline text-[0.65rem] text-[#DE9E48] mb-2">
        Schedule ({entries.length} {entries.length === 1 ? "date" : "dates"})
      </p>
      <div className="space-y-2">
        {entries.map((e, i) => {
          const time = [e.start_time, e.end_time].filter(Boolean).join(" – ");
          return (
            <div
              key={i}
              data-testid={`admin-booking-schedule-row-${i}`}
              className="border border-white/10 rounded-md px-3 py-2 bg-stone-900/40 flex flex-wrap items-baseline gap-x-3 gap-y-1"
            >
              <span className="font-overline text-[0.6rem] text-[#DE9E48] tracking-[0.18em] uppercase">
                {e.type || "—"}
              </span>
              <span className="font-body text-sm text-stone-100">{e.date || "—"}</span>
              {time && <span className="font-body text-sm text-stone-400">· {time}</span>}
              {e.notes && (
                <span className="font-body text-xs text-stone-500 w-full">{e.notes}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AttachmentsDetail({ files }) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="md:col-span-2" data-testid="admin-booking-attachments">
      <p className="font-overline text-[0.65rem] text-[#DE9E48] mb-2">
        Attachments ({files.length})
      </p>
      <ul className="space-y-1.5">
        {files.map((f, i) => (
          <li key={f.id || i}>
            <a
              data-testid={`admin-booking-attachment-${i}`}
              href={`${base}/api/admin/attachments/${f.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm text-[#DE9E48] hover:text-[#F0B770] underline decoration-dotted underline-offset-2"
            >
              {f.filename || f.id}
            </a>
            {typeof f.size === "number" && (
              <span className="font-overline text-[0.6rem] text-stone-500 ml-2">
                {f.size < 1024 * 1024
                  ? `${(f.size / 1024).toFixed(0)} KB`
                  : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------- Account Admin (Change Password) ----------------
function AccountAdmin() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (form.next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (form.next !== form.confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (form.current === form.next) {
      setError("New password must be different from the current password.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      setSuccess(true);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to change password.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="account-admin" className="max-w-xl">
      <div className="mb-8">
        <p className="font-overline text-[0.7rem] text-[#DE9E48] tracking-[0.2em] mb-2">
          — ACCOUNT SECURITY
        </p>
        <h2 className="font-display text-3xl text-stone-50">Change password</h2>
        <p className="font-body text-sm text-stone-400 mt-2 leading-relaxed">
          Signed in as <span className="text-stone-200">{user?.username}</span>.
          Choose a strong password (at least 8 characters). Your session will be
          refreshed after a successful change.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-5 bg-stone-900/40 border border-white/10 rounded-2xl p-6 md:p-8"
      >
        <Field
          label="Current password"
          testId="current-password-input"
          type="password"
          value={form.current}
          onChange={(v) => setForm((f) => ({ ...f, current: v }))}
          autoComplete="current-password"
          required
        />
        <Field
          label="New password"
          testId="new-password-input"
          type="password"
          value={form.next}
          onChange={(v) => setForm((f) => ({ ...f, next: v }))}
          autoComplete="new-password"
          required
        />
        <Field
          label="Confirm new password"
          testId="confirm-password-input"
          type="password"
          value={form.confirm}
          onChange={(v) => setForm((f) => ({ ...f, confirm: v }))}
          autoComplete="new-password"
          required
        />

        {error && (
          <p
            data-testid="account-error"
            className="font-overline text-[0.7rem] text-rose-400"
          >
            × {error}
          </p>
        )}
        {success && (
          <p
            data-testid="account-success"
            className="font-overline text-[0.7rem] text-emerald-400"
          >
            ✓ Password updated successfully.
          </p>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            data-testid="change-password-submit"
            disabled={submitting}
            className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testId, type = "text", value, onChange, autoComplete, required }) {
  return (
    <label className="block">
      <span className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em] block mb-2">
        {label}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="w-full bg-stone-950 border border-white/10 rounded-lg px-4 py-3 text-stone-100 font-body text-sm focus:border-[#DE9E48] focus:outline-none transition-colors"
      />
    </label>
  );
}

